import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { StatusConfig, createStatus, updateStatus } from '@/services/status.service';

interface AddStatusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (status: StatusConfig) => void;
  initialData?: StatusConfig;
  isEdit?: boolean;
  allowedCategories?: ('jobPosting' | 'candidate' | 'interview' | 'project' | 'task')[];
}

interface StatusFormData {
  name: string;
  description: string;
  color: string;
  category: 'jobPosting' | 'candidate' | 'interview' | 'project' | 'task';
  isActive: boolean;
  isDefault: boolean;
}

export function AddStatusDialog({
  open,
  onOpenChange,
  onSubmit,
  initialData,
  isEdit = false,
  allowedCategories
}: AddStatusDialogProps) {
  // Determine default category based on allowedCategories
  const defaultCategory = allowedCategories && allowedCategories.length > 0
    ? allowedCategories[0]
    : 'jobPosting';

  const [formData, setFormData] = useState<StatusFormData>({
    name: '',
    description: '',
    color: '#3B82F6',
    category: defaultCategory,
    isActive: true,
    isDefault: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Color options for statuses
  const colorOptions = [
    { name: 'Blue', value: '#3B82F6' },
    { name: 'Green', value: '#10B981' },
    { name: 'Yellow', value: '#F59E0B' },
    { name: 'Red', value: '#EF4444' },
    { name: 'Purple', value: '#8B5CF6' },
    { name: 'Pink', value: '#EC4899' },
    { name: 'Indigo', value: '#6366F1' },
    { name: 'Gray', value: '#6B7280' },
    { name: 'Slate', value: '#64748B' },
    { name: 'Emerald', value: '#059669' },
  ];

  useEffect(() => {
    if (initialData && isEdit) {
      setFormData({
        name: initialData.name,
        description: initialData.description || '',
        color: initialData.color,
        category: initialData.category,
        isActive: true, // Default to true for existing statuses
        isDefault: initialData.isDefault || false,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        color: '#3B82F6',
        category: defaultCategory,
        isActive: true,
        isDefault: false,
      });
    }
    setErrors({});
  }, [initialData, isEdit, open, defaultCategory]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (field: keyof StatusFormData) => (value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user makes a selection
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };


  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Status name is required';
    }

    if (!formData.description?.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (!formData.color) {
      newErrors.color = 'Color is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {


      if (isEdit) {
        if (!initialData || !initialData.id) {
          toast.error('Invalid status ID for update');
          return;
        }

        const updatedStatus = await updateStatus(initialData.id, {
          name: formData.name.trim(),
          description: formData.description.trim(),
          color: formData.color,
          category: formData.category,
          isActive: formData.isActive,
          isDefault: formData.isDefault,
        });

        // Ensure id is present
        const id = updatedStatus.id || (updatedStatus as any)._id;
        onSubmit({ ...updatedStatus, id });
      } else {
        const newStatus = await createStatus({
          name: formData.name.trim(),
          description: formData.description.trim(),
          color: formData.color,
          category: formData.category,
          isActive: formData.isActive,
          isDefault: formData.isDefault,
        });

        // Ensure id is present
        const id = newStatus.id || (newStatus as any)._id;
        onSubmit({ ...newStatus, id });
      }
      onOpenChange(false);
    } catch (error: any) {
      console.error('Status submission error:', error);
      console.error('Error details:', error.response?.data);

      let errorMessage = isEdit ? 'Failed to update status' : 'Failed to add status';

      if (error.response) {
        if (error.response.status === 409) {
          errorMessage = 'Status name already exists';
        } else if (error.response.status === 403) {
          errorMessage = 'You do not have permission to ' + (isEdit ? 'edit' : 'create') + ' statuses';
        } else if (error.response.status === 404 && isEdit) {
          errorMessage = 'Status not found';
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection';
      }

      toast.error(errorMessage);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Edit Status' : 'Add New Status'}
          </DialogTitle>
          <DialogDescription className="sr-only">Configuration form to define or update application statuses.</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6">
          <div className="space-y-6 py-4">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Status Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Label htmlFor="name" className="text-sm font-medium">Name <span className="text-red-500">*</span></Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter status name"
                    className={errors.name ? 'border-red-500' : ''}
                  />
                  {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
                </div>

                <div className="space-y-3">
                  <Label htmlFor="category" className="text-sm font-medium">Category <span className="text-red-500">*</span></Label>
                  <Select
                    value={formData.category}
                    onValueChange={handleSelectChange('category')}
                  >
                    <SelectTrigger className={errors.category ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {(!allowedCategories || allowedCategories.includes('jobPosting')) && (
                        <SelectItem value="jobPosting">Job Postings</SelectItem>
                      )}
                      {(!allowedCategories || allowedCategories.includes('candidate')) && (
                        <SelectItem value="candidate">Candidates</SelectItem>
                      )}
                      {(!allowedCategories || allowedCategories.includes('interview')) && (
                        <SelectItem value="interview">Interviews</SelectItem>
                      )}
                      {(!allowedCategories || allowedCategories.includes('project')) && (
                        <SelectItem value="project">Projects</SelectItem>
                      )}
                      {(!allowedCategories || allowedCategories.includes('task')) && (
                        <SelectItem value="task">Tasks</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  {errors.category && <p className="text-red-500 text-sm">{errors.category}</p>}
                </div>
              </div>

              <div className="space-y-3">
                <Label htmlFor="description" className="text-sm font-medium">Description <span className="text-red-500">*</span></Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter status description"
                  rows={3}
                  className={errors.description ? 'border-red-500' : ''}
                />
                {errors.description && <p className="text-red-500 text-sm">{errors.description}</p>}
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-medium">Color <span className="text-red-500">*</span></Label>
                <div className="grid grid-cols-5 gap-2">
                  {colorOptions.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      className={`w-8 h-8 rounded-full border-2 transition-all ${formData.color === color.value
                        ? 'border-gray-900 scale-110'
                        : 'border-gray-300 hover:border-gray-400'
                        }`}
                      style={{ backgroundColor: color.value }}
                      onClick={() => setFormData(prev => ({ ...prev, color: color.value }))}
                      title={color.name}
                    />
                  ))}
                </div>
                {errors.color && <p className="text-red-500 text-sm">{errors.color}</p>}
              </div>
            </div>

            <div className="flex items-center justify-between p-4 border rounded-xl bg-gray-50/50">
              <div className="flex items-center space-x-2">
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isActive: checked }))}
                />
                <Label htmlFor="isActive" className="text-sm font-medium cursor-pointer">Active Status</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="isDefault"
                  checked={formData.isDefault}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isDefault: checked }))}
                />
                <Label htmlFor="isDefault" className="text-sm font-medium cursor-pointer">Default Status</Label>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit}>
            {isEdit ? 'Update Status' : 'Add Status'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 